import React, { useState } from "react";
import { Box, Typography, Button, Stack, Divider, Grid } from "@mui/material";
import { useNavigate } from "react-router-dom";
import SolarPowerIcon from "@mui/icons-material/SolarPower";
import GridOffIcon from "@mui/icons-material/GridOff";
import "./Plan.css";

// Plan Options Data
const planOptions = [
  {
    key: "onGrid",
    title: "On-Grid System",
    description:
      "Connected to the public electricity grid. Cost-effective and ideal for homes in cities or towns with stable power supply.",
    buttonLabel: "Plan On-Grid",
    icon: <SolarPowerIcon color="primary" fontSize="large" sx={{ mb: 1 }} />,
    path: "/planner/onGrid",
    variant: "contained",
    useCases: [
      {
        title: "Government Subsidy Eligible",
        note: "Get up to 40% subsidy under national rooftop solar schemes.",
      },
      {
        title: "Drastically Lower Bills",
        note: "Export excess power to the grid and reduce monthly electricity costs.",
      },
      {
        title: "No Battery Needed",
        note: "Cost-effective setup without the need for expensive battery backups.",
      },
    ],
  },
  {
    key: "offGrid",
    title: "Off-Grid System",
    description:
      "Fully independent setup using batteries. Great for remote or rural areas without reliable grid access.",
    buttonLabel: "Plan Off-Grid",
    icon: <GridOffIcon color="primary" fontSize="large" sx={{ mb: 1 }} />,
    path: "/planner/offGrid",
    variant: "contained",
    useCases: [
      {
        title: "No Monthly Electricity Bills",
        note: "Generate and consume your own power — total freedom from rising grid rates.",
      },
      {
        title: "Perfect for Rural & Remote Homes",
        note: "Ideal where grid access is poor or unavailable — reliable, standalone power.",
      },
      {
        title: "Power During Outages",
        note: "Battery storage keeps your home running even when the grid goes down.",
      },
    ],
  },
];

// Component to show use cases
const PlanUseCase = ({ useCases }) => (
  <div className="planusecase-spacing">
    {useCases.map(({ title, note }, index) => (
      <div key={index} className="planusecase-box">
        <Typography variant="subtitle1" fontWeight="bold">
          {title}
        </Typography>
        <Typography variant="body2" mt={1}>
          {note}
        </Typography>
      </div>
    ))}
  </div>
);


// Reusable PlanCard
const PlanCard = ({
  title,
  description,
  icon,
  buttonLabel,
  path,
  variant,
  onHover,
  onLeave,
}) => {
  const navigate = useNavigate();

  return (
    <Box
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      sx={{
        p: 3,
        flex: 1,
        border: "1px solid #ddd",
        borderRadius: 2,
        textAlign: "center",
        transition: "box-shadow 0.3s",
        "&:hover": {
          boxShadow: 3,
        },
      }}
    >
      <Typography variant="h6" gutterBottom>
        {icon} {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2} minHeight={70}>
        {description}
      </Typography>
      <Button fullWidth variant={variant} onClick={() => navigate(path)}>
        {buttonLabel}
      </Button>
    </Box>
  );
};

// Main
const PlanWelcome = () => {
  const [hoveredKey, setHoveredKey] = useState(null);

  const hoveredOption = planOptions.find((opt) => opt.key === hoveredKey);

  return (
    <Box px={2} maxWidth="900px" mx="auto">
      <Typography variant="h4" gutterBottom textAlign="center">
        Welcome to Solar Planner
      </Typography>

      <Typography
        variant="body1"
        color="text.secondary"
        mb={4}
        textAlign="center"
      >
        Select the type of solar system you'd like to explore. We'll guide you
        step-by-step through planning, calculating, and reviewing your solar
        installation.
      </Typography>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={3}
        justifyContent="center"
        divider={<Divider orientation="vertical" flexItem />}
      >
        {planOptions.map((option) => (
          <PlanCard
            key={option.key}
            {...option}
            onHover={() => setHoveredKey(option.key)}
            onLeave={() => setHoveredKey(null)}
          />
        ))}
      </Stack>

      {/* Hover Info below */}
      {hoveredOption && <PlanUseCase useCases={hoveredOption.useCases} />}
    </Box>
  );
};

export default PlanWelcome;
